import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { Loader2 } from "lucide-react";
import { authClient } from "@/lib/auth/auth-client";
import { dispatchAfterLogin } from "@/server/auth-access.functions";

export const Route = createFileRoute("/post-login")({
  head: () => ({ meta: [{ name: "robots", content: "noindex,nofollow" }] }),
  component: PostLogin,
});

// Dispatches a freshly-authenticated user (typically returning from Google OAuth)
// to the right destination based on role + onboarding state. Role provisioning
// and the provider-listing check run server-side (dispatchAfterLogin).
function PostLogin() {
  const navigate = useNavigate();
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Wait briefly for session hydration after the OAuth round-trip.
      let signedIn = false;
      for (let i = 0; i < 20; i++) {
        const { data } = await authClient.getSession();
        if (data?.user) { signedIn = true; break; }
        await new Promise((r) => setTimeout(r, 150));
      }
      if (cancelled) return;

      const role = (typeof window !== "undefined"
        ? localStorage.getItem("selected_login_role")
        : null) as "patient" | "provider" | "admin" | null;

      if (!signedIn) {
        navigate({ to: role ? (`/login/${role}` as any) : "/login" });
        return;
      }

      const intent =
        typeof window !== "undefined" ? localStorage.getItem("heallexa_signup_account_type") : null;

      let result: { target: string; providerHasListing?: boolean };
      try {
        result = await dispatchAfterLogin({ data: { role, intent } });
      } catch {
        // Server dispatch unreachable — let the /dashboard index route by the
        // user's real roles rather than trusting the localStorage selection.
        if (!cancelled) navigate({ to: "/dashboard" });
        return;
      }
      if (cancelled) return;

      if (typeof window !== "undefined") {
        localStorage.removeItem("heallexa_signup_account_type");
        localStorage.removeItem("selected_login_role");
      }

      // Honor pending redirect (e.g. booking flow returning to a provider page).
      const pendingRedirect =
        typeof window !== "undefined" ? localStorage.getItem("heallexa_post_login_redirect") : null;
      if (pendingRedirect && pendingRedirect.startsWith("/")) {
        localStorage.removeItem("heallexa_post_login_redirect");
        navigate({ to: pendingRedirect as any });
        return;
      }

      // dispatchAfterLogin already validated the target against the account's
      // real roles, so go straight to the dashboard (the /dashboard layout
      // still enforces the admin/doctor MFA-enrollment gate).
      if (result.target === "provider") {
        if (result.providerHasListing) {
          navigate({ to: "/dashboard/doctor", search: {} as any });
        } else {
          navigate({ to: "/dashboard/new-listing", search: {} as any });
        }
      } else if (result.target === "admin") {
        navigate({ to: "/dashboard/admin" });
      } else {
        navigate({ to: "/dashboard/patient", search: { tab: undefined } });
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center text-muted-foreground gap-2">
      <Loader2 className="h-4 w-4 animate-spin" /> Signing you in…
    </div>
  );
}
